import * as React from "react";
import { cva } from "class-variance-authority";

import { cn } from "../lib/utils";
import { AiMarker } from "./ai-marker";
import { Icon, type IconComponent } from "./icon";

/**
 * v2 Badge (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap.
 *
 * One appearance: soft tint + strong text, no border (break #47). Tones carry
 * meaning, so there is no blue "brand" tone: blue means action. The AI tone is
 * `--ai-subtle` + `--ink` and carries the AI marker by default (break #103):
 * an AI badge is always tint + marker + text. Pass another `icon` to replace
 * it.
 *
 * Not interactive: no hover, no focus (break #51). Removable chips are the
 * later Chip family.
 *
 * Text never wraps. The label truncates once the caller limits the width
 * (`className="max-w-40"`); pass the full text as `title` or a Tooltip then.
 */

export type BadgeTone = "neutral" | "success" | "warning" | "error" | "info" | "ai";
export type BadgeSize = "sm" | "xs";

const badgeVariants = cva(
  "inline-flex max-w-full shrink-0 items-center whitespace-nowrap rounded-sm align-middle font-sans",
  {
    variants: {
      tone: {
        // On a `--primary-subtle` row the fill vanishes; a 1px inset `--muted-foreground` ring keeps the
        // shape (3.93 / 5.45:1). The row is `group/item` + `data-selected` (selected list / tree row, active
        // NavItem; break #173) or `data-highlighted` (linked FactCard / SourceCard row, not a selection; #188).
        neutral:
          "bg-muted text-ink-muted group-data-selected/item:ring-1 group-data-selected/item:ring-muted-foreground group-data-selected/item:ring-inset group-data-highlighted/item:ring-1 group-data-highlighted/item:ring-muted-foreground group-data-highlighted/item:ring-inset",
        success: "bg-success-subtle text-success-strong",
        warning: "bg-warning-subtle text-warning-strong",
        error: "bg-destructive-subtle text-destructive-strong",
        info: "bg-info-subtle text-info-strong",
        ai: "bg-ai-subtle text-ink",
      } satisfies Record<BadgeTone, string>,
      size: {
        sm: "h-5 gap-1 px-1.5 text-caption font-semibold",
        xs: "h-4 gap-0.5 px-1 text-micro",
      } satisfies Record<BadgeSize, string>,
    },
    defaultVariants: { tone: "neutral", size: "sm" },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** `sm` 20px / 12px text; `xs` 16px / 11px, for inline labels (was SystemBadge). */
  size?: BadgeSize;
  /** Leading icon at 12px. `tone="ai"` defaults to the AI marker. */
  icon?: IconComponent;
  /** Leading status dot in the text colour. Ignored when `icon` is set. */
  dot?: boolean;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { tone = "neutral", size = "sm", icon: iconProp, dot = false, className, children, ...props },
  ref,
) {
  const icon = iconProp ?? (tone === "ai" ? AiMarker : undefined);
  return (
    <span ref={ref} className={cn(badgeVariants({ tone, size }), className)} {...props}>
      {icon ? (
        <Icon icon={icon} size={12} />
      ) : dot ? (
        <span
          data-slot="dot"
          aria-hidden="true"
          className={cn("shrink-0 rounded-full bg-current", size === "xs" ? "size-1" : "size-1.5")}
        />
      ) : null}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
});

export { Badge, badgeVariants };
